// src/components/ProjectManagement/WorkerSidebar.jsx
import React, { useState, useEffect, useRef } from 'react';
import { db } from '../../firebase/config';
import { collection, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import WorkerDropZone from './WorkerDropZone';

// Global cache for worker data
let cachedWorkers = [];
let lastFetchTime = 0;
const CACHE_DURATION = 30000; // 30 seconds cache
let isFetching = false;
let fetchPromise = null;

const WorkerSidebar = () => {
  const [workers, setWorkers] = useState(cachedWorkers);
  const [loading, setLoading] = useState(!cachedWorkers.length);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const calculateWorkerLoad = async (forceRefresh = false) => {
    const now = Date.now();
    
    // Return cached data if available and not expired
    if (!forceRefresh && cachedWorkers.length > 0 && (now - lastFetchTime) < CACHE_DURATION) {
      setWorkers(cachedWorkers);
      setLoading(false);
      return cachedWorkers;
    }
    
    // Prevent multiple simultaneous fetches
    if (isFetching) {
      if (fetchPromise) {
        const result = await fetchPromise;
        setWorkers(result);
        setLoading(false);
        return result;
      }
      return;
    }
    
    isFetching = true;
    setLoading(true);
    
    fetchPromise = (async () => {
      try {
        // OPTIMIZED: Fetch users and tasks in PARALLEL
        const [usersSnapshot, tasksSnapshot] = await Promise.all([
          getDocs(collection(db, 'users')),
          getDocs(query(collection(db, 'tasks'), where('status', 'in', ['pending', 'ongoing'])))
        ]);
        
        
        const usersData = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const tasks = tasksSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // OPTIMIZED: Create a Map for O(1) lookups instead of nested loops
        const taskCountMap = new Map();
        
        tasks.forEach(task => {
          // Handle different assignment formats
          let assignedWorkers = [];
          
          if (Array.isArray(task.assignedTo)) {
            assignedWorkers = task.assignedTo;
          } else if (typeof task.assignedTo === 'string') {
            assignedWorkers = [task.assignedTo];
          } else if (task.assignedTo && typeof task.assignedTo === 'object') {
            assignedWorkers = Object.values(task.assignedTo);
          }
          
          assignedWorkers.forEach(workerId => {
            taskCountMap.set(workerId, (taskCountMap.get(workerId) || 0) + 1);
          });
        });
        
        // Calculate load for each worker (O(n) instead of O(n²))
        const workersWithLoad = usersData.map(user => ({
          ...user,
          currentLoad: taskCountMap.get(user.id) || 0
        }));
        
        // Sort by load (least busy first)
        const sortedWorkers = workersWithLoad.sort((a, b) => a.currentLoad - b.currentLoad);
        
        // Update cache
        cachedWorkers = sortedWorkers;
        lastFetchTime = Date.now();
        
        return sortedWorkers;
      } catch (error) {
        console.error('Error calculating worker load:', error);
        return cachedWorkers.length ? cachedWorkers : [];
      } finally {
        isFetching = false;
        fetchPromise = null;
        setLoading(false);
      }
    })();
    
    const result = await fetchPromise;
    setWorkers(result);
    return result;
  };

  // OPTIMIZED: Use a debounced listener for task updates
  useEffect(() => {
    let debounceTimer;
    
    // Only listen to status changes (pending/ongoing), not all tasks
    const unsubscribe = onSnapshot(
      query(collection(db, 'tasks'), where('status', 'in', ['pending', 'ongoing'])),
      () => {
        // Debounce the refresh to avoid too many updates
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          calculateWorkerLoad(true); // Force refresh on task changes
        }, 500);
      }
    );
    
    // Initial load
    calculateWorkerLoad();
    
    return () => {
      clearTimeout(debounceTimer);
      unsubscribe();
    };
  }, []);

  // Optional: Force refresh every minute to keep data fresh
  useEffect(() => {
    const interval = setInterval(() => {
      if (Date.now() - lastFetchTime > 60000) { // 1 minute
        calculateWorkerLoad(true);
      }
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);

  if (loading && !cachedWorkers.length) {
    return (
      <div className="workers-sidebar">
        <div className="workers-header">
          <h3>👥 Team Members</h3>
        </div>
        <div className="skeleton-workers-list">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="skeleton-worker-card"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="workers-sidebar">
      <div className="workers-header">
        <h3>👥 Team Members</h3>
        <span className="worker-count">{workers.length} members</span>
      </div>
      <p className="workers-subtitle">Drag tasks here to assign to anyone</p>
      
      <div className="workers-list">
        {workers.map(worker => (
          <WorkerDropZone key={worker.id} worker={worker} />
        ))}
      </div>
      
      {/* Optional: Show when data is from cache */}
      {cachedWorkers.length > 0 && (Date.now() - lastFetchTime) > 25000 && (
        <div className="cache-indicator">
          ⚡ Cached data ({(lastFetchTime / 1000).toFixed(0)}s ago)
        </div>
      )}
    </div>
  );
};

export default WorkerSidebar;